import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SongPageShell from "../components/song/SongPageShell";
import SongJourneyTimeline from "../components/song/SongJourneyTimeline";
import InvalidOrderLink from "../components/song/InvalidOrderLink";
import useSongOrderParams from "../hooks/useSongOrderParams";
import useSongOrderNavigation from "../hooks/useSongOrderNavigation";
import useSongOrderSummary from "../hooks/useSongOrderSummary";
import { SONG_JOURNEY_STAGES } from "@/types/SongOrder";
import { clearLastOrderRef, clearAwaitingTrackSong } from "@/utils/onboardingSession";
import { fireSongOrderMailEvent } from "@/utils/songOrderMailEvent";

/** Stages already complete when the page opens. */
const INITIAL_COMPLETED_STAGES = 3;

/** Delay before each remaining stage ticks over. */
const STAGE_ADVANCE_INTERVAL_MS = 1000;

export default function TrackSongPage() {
  const { t } = useTranslation();
  const { docId, userId } = useSongOrderParams();
  const { goToNewSong } = useSongOrderNavigation();
  const { data: summary, isLoading } = useSongOrderSummary(docId, userId);
  const [completedCount, setCompletedCount] = useState(INITIAL_COMPLETED_STAGES);
  const hasFiredMailEvent = useRef(false);

  useEffect(() => {
    if (docId) {
      clearLastOrderRef();
      clearAwaitingTrackSong();
    }
  }, [docId]);

  useEffect(() => {
    if (!docId || isLoading || hasFiredMailEvent.current) return;
    hasFiredMailEvent.current = true;

    void fireSongOrderMailEvent({
      docId,
      userId,
      recipientName: summary?.recipientName ?? "",
      occasion: summary?.occasion ?? "",
      musicStyle: summary?.musicStyle ?? "",
    });
  }, [docId, userId, isLoading, summary]);

  // Generic stand-in so the stage copy reads correctly before the order loads.
  const recipientName =
    summary?.recipientName || t("songOrder.fallback.recipientName");

  // Presentation only — the timeline fills the remaining stages one per second.
  useEffect(() => {
    if (completedCount >= SONG_JOURNEY_STAGES.length) return;

    const timer = setTimeout(
      () => setCompletedCount((count) => count + 1),
      STAGE_ADVANCE_INTERVAL_MS,
    );
    return () => clearTimeout(timer);
  }, [completedCount]);

  if (!docId) {
    return (
      <SongPageShell>
        <InvalidOrderLink onCreateNewSong={goToNewSong} />
      </SongPageShell>
    );
  }

  const footer = (
    <div className="flex w-full items-center justify-center sm:mx-auto sm:max-w-[1100px]">
      <button
        type="button"
        id="trackSongCreateNewSong"
        onClick={goToNewSong}
        className="primary-cta flex-none h-[47px] sm:h-auto uppercase rounded-full px-8 sm:px-16 py-3 sm:py-[20px] !text-[14px] sm:!text-[18px] font-bold tracking-[0.36px] transition-colors"
      >
        {t("songOrder.actions.createNewSong")}
      </button>
    </div>
  );

  return (
    <SongPageShell footer={footer}>
      <div className="mx-auto flex w-full max-w-[720px] flex-col px-8 py-8 sm:py-24">
        <h1 className="text-start text-[18px] sm:text-[42px] pl-[35px] sm:pl-[45px] font-semibold leading-[1.2] tracking-[0.4px] text-[#16283d] sm:text-[40px] dark:text-[#fdfdfd]">
          {summary?.recipientName
            ? t("songOrder.journey.heading", { name: summary.recipientName })
            : t("songOrder.journey.headingFallback")}
        </h1>

        <div className="mt-[15px] flex justify-center sm:mt-12">
          <SongJourneyTimeline
            completedCount={completedCount}
            recipientName={recipientName}
            docId={docId}
            userId={userId}
          />
        </div>
      </div>
    </SongPageShell>
  );
}
